import React, { useEffect, useState, useContext } from 'react';
import Box from '@material-ui/core/Box';
import ButtonUI from '@material-ui/core/Button';
import { useNotify, useRefresh } from 'react-admin';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import RecurringScheduleRow from './RecurringScheduleRow';
import { apiPut } from '../../brainApi';
import {
  fetchRecurringScheduleDetails,
  getScheduleStats,
} from './scheduleApiHelpers';
import { getLengthOfDay } from './SingleDayAvailabilityForm';
import { CalendarEditAvailabilityContext } from '../../Context';
import { getZ3PConfigValue } from '../../helper';

const useStyles = makeStyles(() => ({
  tableRoot: {
    marginLeft: '14px',
  },
  tableHead: {
    fontSize: '12px',
    borderBottom: 'none',
    paddingBottom: 0,
  },
  rowGrid: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    margin: 'auto 40px',
    marginTop: '20px !important',
  },
}));

const RecurringSchedule = () => {
  const classes = useStyles();
  const {
    recurringDayDetails,
    setRecurringDayDetails,
    selectedVehicle,
    scheduleData,
    setScheduleData,
    start,
    end,
    setRefreshExceptionTable,
    isValidToSubmit,
    setIsValidToSubmit,
    allScheduleTypes,
    recurringDayErrors,
    setSelectedRecurringDays,
  } = useContext(CalendarEditAvailabilityContext);
  const { id: serviceVehicleId } = selectedVehicle;
  const notify = useNotify();
  const refresh = useRefresh();
  const [isSaving, setIsSaving] = useState(false);
  const [showScheduleTypeError, setShowScheduleTypeError] = useState(false);

  const { is_using_time_blocks: isUsingTimeBlocks } = getZ3PConfigValue();

  useEffect(() => {
    const hasFieldError = recurringDayErrors.some((day) => day);
    const isAnyDataChanged = recurringDayDetails.some(
      (dayDetails) =>
        dayDetails.is_changed &&
        (!dayDetails.is_new || !dayDetails.is_canceled),
    );
    const schedulesMissingServiceType = recurringDayDetails.some(
      (dayDetails) =>
        !dayDetails.is_canceled &&
        dayDetails.offered_service_types.length === 0,
    );
    setShowScheduleTypeError(
      isAnyDataChanged &&
        allScheduleTypes.length <= 1 &&
        schedulesMissingServiceType,
    );
    setIsValidToSubmit(
      !hasFieldError && isAnyDataChanged && !schedulesMissingServiceType,
    );
    const updatedSelectedDays = recurringDayDetails.map(
      (dayDetails) => !dayDetails.is_canceled,
    );
    setSelectedRecurringDays(updatedSelectedDays);
  }, [recurringDayDetails, recurringDayErrors]);

  const handleSubmitRecurringSchedule = () => {
    setIsSaving(true);
    const recurringSchedulesToSubmit = recurringDayDetails.filter(
      (dayDetails) => {
        // Filter to only submit recurring schedules that have been changed
        // Only include new schedules if they are not canceled - this avoids submitting a schedule that a user toggled on then off
        return (
          dayDetails.is_changed &&
          (!dayDetails.is_new || !dayDetails.is_canceled)
        );
      },
    );
    recurringSchedulesToSubmit.forEach((s) => {
      s.limit_total_hours = getLengthOfDay(s.start_time, s.end_time);
      if (!s.limit_total_driving_hours) s.limit_total_driving_hours = 0;
    });
    apiPut('/recurring-schedule', recurringSchedulesToSubmit)
      .then(() => {
        fetchRecurringScheduleDetails(selectedVehicle, scheduleData)
          .then((recurringSchedules) => {
            setRecurringDayDetails(recurringSchedules);
            setIsSaving(false);
            notify('Weekly schedule was updated successfully!');
          })
          .catch((error) => {
            notify(error.message, 'error');
            setIsSaving(false);
          });
        getScheduleStats(serviceVehicleId, start, end).then((data) => {
          setScheduleData(data);
        });
        setRefreshExceptionTable(true);
        refresh();
      })
      .catch((error) => {
        notify(error.message, 'error');
        setIsSaving(false);
      });
  };

  return (
    <Box style={{ textAlign: 'center' }}>
      <Box>
        {isUsingTimeBlocks && (
          <Grid
            className={classes.rowGrid}
            container
            direction="column"
            justify="space-between"
            xs={12}
            md={8}
            lg={10}
            xl={8}
            style={{ margin: 'auto' }}
          >
            <Grid item xs={1}>
              <h5 className={classes.tableHead}>Bookable</h5>
            </Grid>
            <Grid item xs={1}>
              <span>&nbsp;</span>
            </Grid>
            <Grid item xs={5}>
              <Grid container xs={12}>
                <Grid item xs={5}>
                  <h5 className={classes.tableHead}>Start of Day</h5>
                </Grid>
                <Grid item xs={2} />
                <Grid item xs={5}>
                  <h5 className={classes.tableHead}>End of Day</h5>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={1}>
              <span>&nbsp;</span>
            </Grid>
          </Grid>
        )}
        {recurringDayDetails.map((dayDetails, index) => (
          <RecurringScheduleRow
            key={index}
            dayDetails={dayDetails}
            isServiceBay={selectedVehicle.is_service_bay}
            rowIndex={index}
          />
        ))}
      </Box>
      <Box>
        {showScheduleTypeError && (
          <Typography>
            To set schedule,{' '}
            {selectedVehicle.is_service_bay ? 'service bay' : 'service vehicle'}{' '}
            must be assigned a schedule type.{' '}
            <Link
              href={`#/service-vehicle/${selectedVehicle.id}`}
              color="primary"
            >
              <span>Click here.</span>
            </Link>
          </Typography>
        )}
        <ButtonUI
          variant="contained"
          color="primary"
          display="flex"
          style={{ margin: '15px', justifyContent: 'center' }}
          onClick={handleSubmitRecurringSchedule}
          disabled={!isValidToSubmit || isSaving}
          startIcon={isSaving ? <CircularProgress size={24} /> : null}
        >
          Save
        </ButtonUI>
      </Box>
    </Box>
  );
};

export default RecurringSchedule;
